import { create } from 'zustand';
import {
  getCustomerInfo,
  getOfferings,
  onCustomerInfoUpdate,
  purchase as purchasePackage,
  restore as restorePurchases,
  type NormalizedCustomerInfo,
  type NormalizedOfferings,
  type NormalizedPackage,
} from '../services/purchasesService';
import { friendlyErrorMessage } from '../utils/errorCopy';

export type SubscriptionStatus = 'unknown' | 'loading' | 'ready' | 'error';

export interface SubscriptionState {
  status: SubscriptionStatus;
  isPro: boolean;
  offerings: NormalizedOfferings['current'] | null;
  customerInfo: NormalizedCustomerInfo | null;
  error: string | null;

  /** Scan quota reflected from /v1/ocr/* response headers. `null` until the
   *  app makes its first scan call — `ready` status does NOT imply quota is
   *  populated. */
  scansUsedThisMonth: number | null;
  scansCapThisMonth: number | 'unlimited' | null;
  scansResetAtUtc: string | null;

  refresh: () => Promise<void>;
  purchase: (pkg: NormalizedPackage) => Promise<NormalizedCustomerInfo>;
  restore: () => Promise<NormalizedCustomerInfo>;
  setServerQuota: (args: {
    used: number;
    cap: number | 'unlimited';
    resetAtUtc?: string | null;
  }) => void;
  ingestCustomerInfo: (info: NormalizedCustomerInfo) => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => {
  // Listen for RC customer info updates once at store creation. The wrapper's
  // listener survives across logins; updates flow in whenever RC detects a
  // subscription event (purchase, renewal, cancellation).
  onCustomerInfoUpdate((info) => {
    get().ingestCustomerInfo(info);
  });

  return {
    status: 'unknown',
    isPro: false,
    offerings: null,
    customerInfo: null,
    error: null,
    scansUsedThisMonth: null,
    scansCapThisMonth: null,
    scansResetAtUtc: null,

    async refresh() {
      const prev = get().status;
      if (prev !== 'ready') set({ status: 'loading', error: null });
      try {
        const [info, offerings] = await Promise.all([
          getCustomerInfo(),
          getOfferings(),
        ]);
        set({
          status: 'ready',
          isPro: info.isPro,
          customerInfo: info,
          offerings: offerings.current,
          error: null,
        });
      } catch (err) {
        const message = friendlyErrorMessage(err, 'Could not load subscription details.');
        set({ status: 'error', error: message });
      }
    },

    async purchase(pkg) {
      const info = await purchasePackage(pkg);
      set({ isPro: info.isPro, customerInfo: info });
      // If the paywall was opened because of a scan_cap 402, the server-side
      // cap will flip to "unlimited" once the refreshed Clerk token lands.
      // Reset the client cache so the UI doesn't show stale "5/5 used".
      if (info.isPro) {
        set({ scansCapThisMonth: 'unlimited', scansUsedThisMonth: null });
      }
      return info;
    },

    async restore() {
      const info = await restorePurchases();
      set({ isPro: info.isPro, customerInfo: info });
      if (info.isPro) {
        set({ scansCapThisMonth: 'unlimited', scansUsedThisMonth: null });
      }
      return info;
    },

    setServerQuota({ used, cap, resetAtUtc }) {
      set({
        scansUsedThisMonth: used,
        scansCapThisMonth: cap,
        scansResetAtUtc: resetAtUtc ?? get().scansResetAtUtc,
      });
    },

    ingestCustomerInfo(info) {
      set({
        isPro: info.isPro,
        customerInfo: info,
        // Once we have any customer info, we at least know the Pro state.
        status: get().status === 'unknown' ? 'ready' : get().status,
      });
    },
  };
});

/** Hook that returns true when the user has the Pro entitlement. */
export function useIsPro(): boolean {
  return useSubscriptionStore((s) => s.isPro);
}
